import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Die eigene Rückfrage — statt `window.confirm`.
 *
 * Der Browserdialog kann den zerstörenden Knopf weder rot machen noch beim
 * Namen nennen: er heißt immer „OK", egal ob dahinter ein Löschen steht.
 * Hier heißt der Knopf, was er tut („Löschen", „Wegnehmen"), trägt die
 * Warnfarbe, und **Abbrechen ist die sichere Vorgabe** — Escape und ein
 * Tipp auf den Hintergrund sagen Nein.
 *
 * Benutzt wie `confirm`: `if (await rueckfrage('Diese Nachricht löschen?',
 * 'Löschen')) …`. Der `RueckfrageHost` steht einmal in der `Shell`; sollte
 * er je fehlen, fällt die Funktion auf `window.confirm` zurück — eine
 * hässliche Rückfrage ist besser als ein stilles Ja.
 */
interface Frage {
  frage: string
  knopf: string
  antwort: (ja: boolean) => void
}

let stellen: ((frage: Frage) => void) | null = null

export function rueckfrage(frage: string, knopf = 'Ja'): Promise<boolean> {
  if (stellen === null) {
    return Promise.resolve(window.confirm(frage))
  }
  const fragen = stellen
  return new Promise((antwort) => fragen({ frage, knopf, antwort }))
}

export function RueckfrageHost() {
  const [offen, setOffen] = useState<Frage | null>(null)

  useEffect(() => {
    stellen = (frage) => setOffen(frage)
    return () => {
      stellen = null
    }
  }, [])

  useEffect(() => {
    if (offen === null) return
    const taste = (event: KeyboardEvent) => {
      if (event.key === 'Escape') beantworten(false)
      // Enter bleibt bewusst unbelegt: die gefährliche Antwort soll ein
      // gezielter Tipp sein, kein Durchwinken.
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen])

  if (offen === null) return null

  function beantworten(ja: boolean) {
    offen?.antwort(ja)
    setOffen(null)
  }

  return createPortal(
    <div
      className="rueckfrage"
      role="alertdialog"
      aria-modal="true"
      aria-label={offen.frage}
      onClick={() => beantworten(false)}
    >
      <div className="rueckfrage__blatt" onClick={(event) => event.stopPropagation()}>
        <p className="rueckfrage__frage">{offen.frage}</p>
        <div className="rueckfrage__knoepfe">
          <button
            type="button"
            className="button button--outline button--small"
            onClick={() => beantworten(false)}
            // Der sichere Weg bekommt den Fokus -- wer blind Enter drückt,
            // löscht nichts.
            autoFocus
          >
            Abbrechen
          </button>
          <button
            type="button"
            className="button button--small rueckfrage__gefahr"
            onClick={() => beantworten(true)}
          >
            {offen.knopf}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
